import { Select } from '@99designs/common/components/Select';
import { formatMessage } from '@99designs/common/i18n';
import {
    PreziBriefFields_inspirationFiles,
    PreziBriefFields_powerpointFiles,
    PreziBriefFields_referenceFiles,
} from '__generated__/PreziBriefFields';
import { PreziProjectType } from '__generated__/types';
import { Uploader } from '__generated__/Uploader';
import { handlePromise } from 'common/utils/Bugsnag';
import DescriptionField from 'projects/components/BriefFields/DescriptionField';
import InspirationFilesField from 'projects/components/BriefFields/InspirationFilesField';
import InspirationUrlsField from 'projects/components/BriefFields/InspirationUrlsField';
import LanguageField from 'projects/components/BriefFields/LanguageField';
import PowerpointFilesField from 'projects/components/BriefFields/PowerpointFilesField';
import PreziUrlsFields from 'projects/components/BriefFields/PreziUrlsFields';
import ReferenceFilesField from 'projects/components/BriefFields/ReferenceFilesField';
import TitleField from 'projects/components/BriefFields/TitleField';
import ContentSetting from 'projects/components/ContentSetting';
import { ObservableField as Field, ObservableForm } from 'projects/components/ObservableForm';
import { MAX_TITLE_LENGTH } from 'projects/consts';
import { trackViewedBriefEvent } from 'projects/events';
import React, { useEffect } from 'react';

interface Fields {
    language: string;
    title: string;
    description: string;
    projectType: PreziProjectType;
    existingPreziUrls: string[];
    preziClassicUrls: string[];
    powerpointFiles: PreziBriefFields_powerpointFiles[];
    referenceFiles: PreziBriefFields_referenceFiles[];
    inspirationFiles: PreziBriefFields_inspirationFiles[];
    inspirationUrls: string[];
}

export interface Props<T> {
    form: ObservableForm<T>;
    brief: {
        id: string;
        uploader: Uploader;
    };
}

const DetailsPage: <T extends Fields>(props: Props<T>) => React.ReactElement<Props<T>> = ({
    form,
    brief,
}) => {
    useEffect(() => {
        trackViewedBriefEvent({
            id: brief.id,
            page: 'Details',
        });
    }, []);
    return (
        <ContentSetting>
            <LanguageField form={form} />
            <TitleField
                form={form}
                placeholderText={formatMessage(
                    __(
                        'E.g. Prezi to pitch Geoengineering: our path to a cooler world (max {maxSize} characters)'
                    ),
                    { maxSize: MAX_TITLE_LENGTH }
                )}
            />

            <Field
                form={form}
                name="projectType"
                title={__('What type of Prezi project do you need?')}
                required={true}
                render={() => (
                    <Select
                        name="projectType"
                        options={[
                            {
                                value: '',
                                disabled: true,
                                label: __('Select project type'),
                            },
                            {
                                value: PreziProjectType.NEW_CUSTOM_PREZI,
                                label: __('Create a new custom Prezi'),
                            },
                            {
                                value: PreziProjectType.EDIT_EXISTING_PREZI,
                                label: __('Edit an existing Prezi'),
                            },
                            {
                                value: PreziProjectType.CONVERT_PPT_TO_PREZI,
                                label: __('Convert a PowerPoint presentation to Prezi'),
                            },
                            {
                                value: PreziProjectType.BRANDED_PREZI_TEMPLATE,
                                label: __('Create a branded Prezi template'),
                            },
                            {
                                value: PreziProjectType.CONVERT_CLASSIC_TO_NEXT,
                                label: __('Convert a Prezi Classic to Prezi Next'),
                            },

                            { value: PreziProjectType.OTHER, label: __('Other') },
                        ]}
                        value={form.get('projectType')}
                        onChange={({ value }) => {
                            handlePromise(
                                form.set('projectType', value as PreziProjectType),
                                'DetailsPage'
                            );
                        }}
                        variations={['max']}
                    />
                )}
            />

            {form.get('projectType') == PreziProjectType.CONVERT_PPT_TO_PREZI && (
                <PowerpointFilesField form={form} uploader={brief.uploader} required={true} />
            )}

            <PreziUrlsFields form={form} projectType={form.get('projectType')} />

            <DescriptionField
                form={form}
                placeholderText={__(
                    'E.g. We need a Prezi explaining our advanced carbon sequestration techniques to a panel of climate scientists at the United Nations. We have about 30 slides worth of information but want a Prezi that really helps communicate our technology dynamically.'
                )}
            />
            <ReferenceFilesField form={form} uploader={brief.uploader} />
            <InspirationFilesField form={form} uploader={brief.uploader} />
            <InspirationUrlsField form={form} />
        </ContentSetting>
    );
};

export default DetailsPage;
